import { useEffect, useMemo, useState } from 'react';
import { filterCommercantsByName } from '../../materiels/materielCommercePicker';

/**
 * Picker point de vente par nom — aucun id affiché.
 */
export default function MaterielCommercePicker({
  commerces = [],
  value = null,
  onChange,
  disabled = false,
  loading = false,
  error = null,
  onRetry,
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  const results = useMemo(
    () => filterCommercantsByName(commerces, debounced),
    [commerces, debounced]
  );

  if (loading) {
    return (
      <div className="space-y-2">
        <div className="h-9 rounded-md bg-gray-100 animate-pulse" />
        <p className="text-xs text-gray-500">Chargement des points de vente…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 space-y-1">
        <p className="text-xs text-red-800">
          Impossible de charger la liste des points de vente.
        </p>
        {typeof onRetry === 'function' ? (
          <button
            type="button"
            onClick={onRetry}
            className="text-xs font-medium text-red-900 underline"
          >
            Réessayer
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <input
        type="search"
        value={query}
        disabled={disabled}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Rechercher un point de vente par nom…"
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
        aria-label="Rechercher un point de vente"
      />
      {value ? (
        <div className="flex items-center justify-between gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-primary-900 truncate">
              {value.nom}
            </p>
            {value.ville ? (
              <p className="text-xs text-primary-700 truncate">{value.ville}</p>
            ) : null}
          </div>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange?.(null)}
            className="text-xs text-primary-800 underline shrink-0"
          >
            Changer
          </button>
        </div>
      ) : (
        <ul className="max-h-48 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100 bg-white">
          {results.length === 0 ? (
            <li className="px-3 py-3 text-sm text-gray-500">
              {debounced.trim()
                ? 'Aucun point de vente ne correspond.'
                : 'Saisissez un nom pour filtrer la liste.'}
            </li>
          ) : (
            results.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onChange?.(c)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 disabled:opacity-60"
                >
                  <span className="text-sm font-medium text-gray-900 block truncate">
                    {c.nom}
                  </span>
                  {c.ville ? (
                    <span className="text-xs text-gray-500">{c.ville}</span>
                  ) : null}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
